import { useQuery } from '@tanstack/react-query';
import { apiFetch } from './api';
import {
  ApiError,
  type ContactCustomObjectsResponse,
  type CustomObjectType,
  type CustomValue,
} from '../types/api';

export const CUSTOM_OBJECT_TYPES_KEY = ['custom-objects', 'types'] as const;
export const contactCustomObjectsKey = (contactId: string) =>
  ['custom-objects', 'contact', contactId] as const;

export function useCustomObjectTypes() {
  const query = useQuery({
    queryKey: CUSTOM_OBJECT_TYPES_KEY,
    queryFn: () => apiFetch<{ items: CustomObjectType[] }>('/custom-objects/types'),
  });
  return { types: query.data?.items ?? [], isLoading: query.isLoading, isError: query.isError };
}

export function useContactCustomObjects(contactId: string) {
  return useQuery({
    queryKey: contactCustomObjectsKey(contactId),
    queryFn: () =>
      apiFetch<ContactCustomObjectsResponse>(
        `/custom-objects/contacts/${encodeURIComponent(contactId)}`,
      ),
  });
}

/** The API's message for a refused write, or `fallback`. */
export function customObjectErrorText(error: unknown, fallback: string): string | null {
  if (!error) return null;
  if (error instanceof ApiError) {
    if (error.status === 403) return 'You do not have permission to do this.';
    if (error.body.message) return error.body.message;
  }
  return fallback;
}

/** A value as one short line of text. */
export function formatCustomValue(value: CustomValue | undefined): string {
  if (value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}
